import { faEnvelope, faExclamationTriangle, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { useMemo } from "react";
import ModalWidget from "./ModalWidget";

import formalIcon from '../../static/formal.png';
import informalIcon from '../../static/informal.png';
import flashFloodIcon from '../../static/flashflood.png';
import { Tooltip } from "@mui/material";
import { NotificationFilters } from "../../hooks/useInteractivePopup";
import type { AlertPoint, EFASNotification } from '../../../efas/notifications/Notification.types';

function ActiveNotification(props: { notification: AlertPoint, onNotificationTitleClicked?: (point: { lat: number, lon: number }) => void }) {
    const date = moment.utc(props.notification.forecast_date, "YYYYMMDDHH");

    const openNotificationPreview = (point_id: number, forecast_date: string) => {
        fetch(`/api/notifications/sent/?format=json&point_id=${point_id}&forecast_date=${forecast_date}`)
            .then(response => response.json())
            .then((data: EFASNotification[]) => {
                const notification = data[0];
                window.open(`/notifications-archive/detail/${notification.notification_id}`, "_blank");
            });
    }

    // region is only for flash notifications and comes from ad2_name
    const region = Array.isArray(props.notification.ad2_name) ? props.notification.ad2_name.join(", ") : props.notification.ad2_name;
    return <div className="d-flex flex-column border-bottom my-2 fs-8">
        <div className="flex-row ms-2">
            <span className="btn p-0">
                <a onClick={() => openNotificationPreview(props.notification.id, props.notification.forecast_date)} target="_blank" title="Open the notification details in a new tab">
                    <FontAwesomeIcon icon={faEnvelope} />
                </a>
            </span>
            <span role="button"
                className={` ms-2 p-1 border border-2 fw-bold rounded border-${props.notification.alert_type === 1 ? 'danger' : props.notification.alert_type === 2 ? 'informal' : 'primary'} text-${props.notification.alert_type === 1 ? 'danger' : props.notification.alert_type === 2 ? 'informal' : 'primary'}`}

                onClick={() => props.onNotificationTitleClicked ? props.onNotificationTitleClicked({ lat: props.notification.location.coordinates[1], lon: props.notification.location.coordinates[0] }) : undefined}
            >{props.notification.country} - {date.format("YYYY-MM-DD")}</span>
        </div>
        <div className="flex-column px-2">
            {region !== "" && <div>Region: {region}</div>
                || <>
                    <div>River: {props.notification.river_name}</div>
                    <div>Basin: {props.notification.basin_name}</div>
                </>
            }
            <div>Forecast Date: {date.format("YYYY-MM-DD HH:00")}</div>
        </div>
    </div>
}

interface ActiveNotificationsWidgetProps {
    allNotifications: AlertPoint[];
    filteredNotifications: AlertPoint[];
    filters: NotificationFilters;
    onFilterChange: (key: keyof NotificationFilters, value: string) => void;
    onNotificationTitleClicked?: (point: { lat: number, lon: number }) => void;
}

export default function ActiveNotificationsWidget(props: ActiveNotificationsWidgetProps) {
    const { allNotifications, filteredNotifications, filters, onFilterChange } = props;

    const partners = useMemo(
        () => Array.from(new Set(allNotifications.flatMap(n => n.partner_abbr.map(p => p.toString())))).sort(),
        [allNotifications]
    );

    const dissPartners = useMemo(
        () => Array.from(new Set(allNotifications.map(n => n.diss_partner).filter(Boolean))).sort(),
        [allNotifications]
    );

    return <div data-testid="active-notifications-widget">
        <div className="d-flex flex-row">
            <div className="d-flex flex-row my-auto">
                <div className="my-auto me-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="p-1" />
                </div>
                <div className="d-flex flex-column">
                    <Tooltip arrow placement="top" disableInteractive title={`Total Notifications: ${allNotifications.length}, Formal: ${allNotifications.filter(n => n.alert_type === 1).length}, Informal: ${allNotifications.filter(n => n.alert_type === 2).length}, Flash: ${allNotifications.filter(n => n.alert_type === 3).length}`}>
                        <h5 role="button" className="m-0" data-testid="active-notifications-count"><span className="fw-bold">Active Notifications</span>: {allNotifications.length}</h5>
                    </Tooltip>
                    {allNotifications.length !== filteredNotifications.length &&
                        <div className="fs-8">Visible based on <strong>current filters</strong>: {filteredNotifications.length}</div>}
                </div>

            </div>
            <div role="button" className="ms-auto">
                <ModalWidget
                    toggleButton={<FontAwesomeIcon icon={faInfoCircle} className="p-1" />}
                    title={"Active Notifications Legend"}>
                    <span>
                        <img src={formalIcon} alt="Formal Notification Icon" />
                        <span>Region or river basin where an EFAS Formal Flood Notification has been sent to.</span>
                    </span>
                    <span>
                        <img src={informalIcon} alt="Informal Notification Icon" />
                        <span>Region or river basin where an EFAS Informal Flood Notification has been sent to.</span>
                    </span>
                    <span>
                        <img src={flashFloodIcon} alt="Flash Flood Notification Icon" />
                        <span>Region or river basin where an EFAS Flash Flood Notification has been sent to.</span>
                    </span>
                </ModalWidget>
            </div>
        </div>
        <div className="d-flex flex-column gap-1 pb-2 mb-1 border-bottom">
            <div className="input-group m-0">
                <Tooltip arrow placement="top" disableInteractive title="Conditions of Access Partner">
                    <label className="input-group-text fs-8">CoA Partner</label>
                </Tooltip>
                <select key={filters.partner} defaultValue={filters.partner} onChange={(e) => {
                    onFilterChange('partner', e.target.value);
                }}
                    className="form-select fs-8"
                    style={{ width: "10rem" }}>

                    <option>All</option>
                    {partners.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
            </div>
            <div className="input-group m-0">
                <Tooltip arrow placement="top" disableInteractive title="Dissemination Centre Partner">
                    <label className="input-group-text fs-8">Diss Partner</label>
                </Tooltip>
                <select key={filters.dissPartner} defaultValue={filters.dissPartner} onChange={(e) => {
                    onFilterChange('dissPartner', e.target.value);
                }}
                    className="form-select fs-8"
                    style={{ width: "10rem" }}>

                    <option>All</option>
                    {dissPartners.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
            </div>
            <div className="input-group m-0">
                <label className="input-group-text fs-8">Notification Type</label>
                <select key={filters.type} defaultValue={filters.type} onChange={(e) => {
                    onFilterChange('type', e.target.value);
                }}
                    style={{ width: "10rem" }}
                    className="form-select fs-8"
                >
                    <option>All</option>
                    <option>Formal</option>
                    <option>Informal</option>
                    <option>Flash</option>
                </select>
            </div>
        </div>
        <div data-testid="active-notifications-list" style={{ overflowY: 'scroll', maxHeight: '29rem' }}>
            {filteredNotifications.map((n) => <ActiveNotification key={n.id} notification={n} onNotificationTitleClicked={props.onNotificationTitleClicked} />)}
        </div>
    </div>

}